import React, { useState, useEffect } from 'react';
import { fetchAgencies, createAgency, updateAgency, deleteAgency } from '../../services/api';
import { toast } from 'react-toastify';
import './AdminComponents.css';

const AgenciesManagement = () => {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Agency form for creating/editing
  const [agencyForm, setAgencyForm] = useState({
    name: '',
    description: '',
    contactEmail: '',
    contactPhone: '',
    address: ''
  });

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      setError('');
      
      const fetchedAgencies = await fetchAgencies();
      setAgencies(fetchedAgencies);
      setLastRefresh(new Date().toLocaleTimeString());
      setLoading(false);
    } catch (err) {
      console.error('Error loading agencies:', err);
      setError('Failed to load agencies. Please try again.');
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadAgencies();
    toast.info('Refreshing agencies data...');
  };

  const handleChange = (e) => {
    setAgencyForm({
      ...agencyForm,
      [e.target.name]: e.target.value
    });
  };

  const handleEditClick = (agency) => {
    setSelectedAgency(agency);
    setAgencyForm({
      name: agency.name || '',
      description: agency.description || '',
      contactEmail: agency.contactEmail || '',
      contactPhone: agency.contactPhone || '',
      address: agency.address || ''
    });
    setIsEditing(true);
    setShowDeleteConfirm(false);
  };

  const handleDeleteClick = (agency) => {
    setSelectedAgency(agency);
    setShowDeleteConfirm(true);
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setSelectedAgency(null);
    setIsEditing(false);
    setAgencyForm({
      name: '',
      description: '',
      contactEmail: '',
      contactPhone: '',
      address: ''
    });
  };

  const handleCreateAgency = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      const response = await createAgency(agencyForm);
      
      // Update the agencies list
      setAgencies([...agencies, response]);
      
      // Reset form
      setAgencyForm({
        name: '',
        description: '',
        contactEmail: '',
        contactPhone: '',
        address: ''
      });
      
      toast.success('Agency created successfully');
      setLoading(false);
    } catch (err) {
      console.error('Error creating agency:', err);
      toast.error(err.response?.data?.message || 'Failed to create agency');
      setLoading(false);
    }
  };

  const handleUpdateAgency = async (e) => {
    e.preventDefault();
    
    if (!selectedAgency) return;
    
    try {
      setLoading(true);
      const response = await updateAgency(selectedAgency._id, agencyForm);
      
      // Update the agencies list
      setAgencies(agencies.map(agency => 
        agency._id === selectedAgency._id ? response : agency
      ));
      
      // Reset form and selected agency
      handleCancelEdit();
      
      toast.success('Agency updated successfully');
      setLoading(false);
    } catch (err) {
      console.error('Error updating agency:', err);
      toast.error(err.response?.data?.message || 'Failed to update agency');
      setLoading(false);
    }
  };

  const handleDeleteAgency = async () => {
    if (!selectedAgency) return;
    
    try {
      setLoading(true);
      
      // Log the ID to ensure we're using the correct format
      console.log(`Attempting to delete agency with ID: ${selectedAgency._id}`);
      
      const response = await deleteAgency(selectedAgency._id);
      console.log('Delete agency response:', response);
      
      // Update the agencies list
      setAgencies(agencies.filter(agency => agency._id !== selectedAgency._id));
      
      // Reset selected agency and close modal
      setSelectedAgency(null);
      setShowDeleteConfirm(false);
      
      // Show success message with details about removed users and updated bookings
      if (response) {
        let successMessage = 'Agency deleted successfully.';
        
        if (response.usersConverted > 0) {
          successMessage += ` ${response.usersConverted} associated users were converted to normal users.`;
        }
        
        if (response.bookingsUpdated > 0) {
          successMessage += ` ${response.bookingsUpdated} bookings were updated.`;
        }
        
        toast.success(successMessage);
      } else {
        toast.success('Agency deleted successfully.');
      }
      
      setLoading(false);
    } catch (err) {
      setLoading(false);
      console.error(`Error deleting agency ${selectedAgency._id}:`, err);
      
      // Detailed error information
      let errorMessage = 'Failed to delete agency.';
      
      if (err.response) {
        console.error('Response status:', err.response.status);
        console.error('Response data:', err.response.data);
        
        // More specific error message based on response
        if (err.response.status === 500) {
          errorMessage = 'Server error while deleting agency.';
          if (err.response.data && err.response.data.error) {
            errorMessage += ` Error details: ${err.response.data.error}`;
          }
        } else if (err.response.data && err.response.data.message) {
          errorMessage = `Error: ${err.response.data.message}`;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  if (loading && !agencies.length) {
    return <div className="loading">Loading agencies...</div>;
  }

  return (
    <div className="admin-component">
      <h2>Agencies Management</h2>
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="controls-row">
        <div className="title-section">
          <h3>All Agencies</h3>
          {lastRefresh && (
            <div className="last-refresh">
              Last refreshed: {lastRefresh}
            </div>
          )}
        </div>
        
        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh Data
        </button>
      </div>
      
      <div className="admin-section create-form">
        <h3>Create New Agency</h3>
        <form onSubmit={handleCreateAgency}>
          <div className="form-group">
            <label htmlFor="name">Agency Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={agencyForm.name}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={agencyForm.description}
              onChange={handleChange}
              rows="3"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="contactEmail">Contact Email</label>
            <input
              type="email"
              id="contactEmail"
              name="contactEmail"
              value={agencyForm.contactEmail}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="contactPhone">Contact Phone</label>
            <input
              type="text"
              id="contactPhone"
              name="contactPhone"
              value={agencyForm.contactPhone}
              onChange={handleChange}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={agencyForm.address}
              onChange={handleChange}
            />
          </div>
          
          <button type="submit" className="submit-btn" disabled={loading}>
            Create Agency
          </button>
        </form>
      </div>
      
      <div className="admin-section agency-list">
        <h3>Existing Agencies</h3>
        
        {agencies.length === 0 ? (
          <div className="no-data">
            No agencies found. Create your first agency above.
          </div>
        ) : (
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Agency Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {agencies.map(agency => (
                  <tr key={agency._id}>
                    <td>{agency.name}</td>
                    <td>{agency.contactEmail}</td>
                    <td>{agency.contactPhone || 'N/A'}</td>
                    <td>{agency.address || 'N/A'}</td>
                    <td className="actions-cell">
                      <button
                        className="action-btn edit-btn"
                        onClick={() => handleEditClick(agency)}
                      >
                        Edit
                      </button>
                      <button
                        className="action-btn delete-btn"
                        onClick={() => handleDeleteClick(agency)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Edit Modal */}
      {isEditing && selectedAgency && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Edit Agency</h3>
            
            <form onSubmit={handleUpdateAgency}>
              <div className="form-group">
                <label htmlFor="edit-name">Agency Name</label>
                <input
                  type="text"
                  id="edit-name"
                  name="name"
                  value={agencyForm.name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  name="description"
                  value={agencyForm.description}
                  onChange={handleChange}
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-contactEmail">Contact Email</label>
                <input
                  type="email"
                  id="edit-contactEmail"
                  name="contactEmail"
                  value={agencyForm.contactEmail}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-contactPhone">Contact Phone</label>
                <input
                  type="text"
                  id="edit-contactPhone"
                  name="contactPhone"
                  value={agencyForm.contactPhone}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="edit-address">Address</label>
                <input
                  type="text"
                  id="edit-address"
                  name="address"
                  value={agencyForm.address}
                  onChange={handleChange}
                />
              </div>
              
              <div className="form-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancelEdit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={loading}
                >
                  Update Agency
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && selectedAgency && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Deletion</h3>
            
            <p>
              Are you sure you want to delete the agency
              <strong> {selectedAgency.name}</strong>?
            </p>
            <p className="warning-text">
              <strong>Note:</strong> All users associated with this agency will be converted to normal users and will no longer have agency access.
            </p>
            <p className="warning-text">
              This action cannot be undone.
            </p>
            
            <div className="form-actions">
              <button
                type="button"
                className="cancel-btn"
                onClick={() => {
                  setSelectedAgency(null);
                  setShowDeleteConfirm(false);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="delete-btn confirm-delete"
                onClick={handleDeleteAgency}
                disabled={loading}
              >
                {loading ? 'Deleting...' : 'Delete Agency'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgenciesManagement; 